<template>
    <div>
        <main-menu></main-menu>

        <section class="wrapper scrollable">

            <user-menu></user-menu>

            <breadcrumb></breadcrumb>

            <div id="top" class="panel panel-default panel-block panel-title-block">
                <div class="panel-heading">
                    <div>
                        <i class="fal fa-chart-bar"></i>
                        <h1 style="margin-top: 5px;">WC KPIs<span class="page-title"></span></h1>
                    </div>
                    <div class="kpi-controls">
                        <div class="btn-group btn-group-sm" role="group">
                            <button v-for="p in presets" :key="p.key"
                                class="btn"
                                :class="range === p.key ? 'btn-primary' : 'btn-default'"
                                @click="selectRange(p.key)">{{ p.label }}</button>
                        </div>
                        <span v-if="range === 'custom'" class="custom-range">
                            <input type="date" v-model="customStart" class="form-control input-sm inline-date">
                            <span>→</span>
                            <input type="date" v-model="customEnd" class="form-control input-sm inline-date">
                            <button class="btn btn-sm btn-primary" @click="applyCustom">Go</button>
                        </span>
                        <span class="updated">
                            <span v-if="snapshotAt">Updated {{ formatRelative(snapshotAt) }}</span>
                            <button class="btn btn-xs btn-default" @click="refresh({ force: true })">
                                <i class="fal fa-sync" :class="{ 'fa-spin': loading }"></i> Refresh
                            </button>
                        </span>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-12">

                    <div v-if="error" class="alert alert-danger">
                        <strong>KPIs unavailable:</strong> {{ error }}
                    </div>

                    <template v-if="kpi">

                    <p class="range-caption text-muted">
                        Showing <strong>{{ kpi.range.label }}</strong>
                        ({{ kpi.range.start }} → {{ kpi.range.end }}) · web / iOS / Android combined unless noted.
                    </p>

                    <!-- ══ ACQUISITION ══════════════════════════════════════ -->
                    <h4 class="kpi-section">Acquisition</h4>
                    <div class="row">
                        <!-- App downloads -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">APP DOWNLOADS</small>
                                    <div class="kpi-value">{{ formatNumber(downloadsTotal) }}</div>
                                    <div class="kpi-sub">
                                        <span v-if="kpi.downloads.ios.available">
                                            iOS <strong>{{ formatNumber(kpi.downloads.ios.downloads) }}</strong>
                                        </span>
                                        ·
                                        <span v-if="kpi.downloads.android.available">
                                            Android <strong>{{ formatNumber(kpi.downloads.android.downloads) }}</strong>
                                        </span>
                                        <span v-else class="text-warning" :title="kpi.downloads.android.reason">
                                            Android pending<i class="fal fa-clock"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Unique visitors -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">UNIQUE VISITORS</small>
                                    <div class="kpi-value">{{ formatNumber(kpi.ga4.totals.total_users) }}</div>
                                    <div class="kpi-sub">{{ platformMix }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Registered users -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">REGISTERED USERS</small>
                                    <div class="kpi-value">
                                        <span class="text-success">+{{ formatNumber(kpi.ga4.totals.signups) }}</span>
                                    </div>
                                    <div class="kpi-sub">
                                        new this range · <strong>{{ formatNumber(kpi.local.registered_total) }}</strong> total
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- CAC -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">CAC <span class="text-muted">(cost / acquired)</span></small>
                                    <div class="kpi-value">
                                        <span v-if="kpi.manual.cac !== null">{{ formatMoney(kpi.manual.cac, kpi.manual.currency) }}</span>
                                        <span v-else class="kpi-empty">—</span>
                                    </div>
                                    <div class="kpi-sub">
                                        <span v-if="kpi.manual.ad_spend !== null">
                                            {{ formatMoney(kpi.manual.ad_spend, kpi.manual.currency) }} spend ÷ {{ formatNumber(kpi.manual.acquired) }}
                                        </span>
                                        <a v-else href="#" @click.prevent="editing = true">set ad spend</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ══ ENGAGEMENT ═══════════════════════════════════════ -->
                    <h4 class="kpi-section">Engagement</h4>
                    <div class="row">
                        <!-- DAU/WAU/MAU -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">DAU / WAU / MAU</small>
                                    <div class="kpi-value">
                                        {{ formatNumber(kpi.ga4.totals.dau) }}<span class="kpi-slash">/</span>{{ formatNumber(kpi.ga4.totals.wau) }}<span class="kpi-slash">/</span>{{ formatNumber(kpi.ga4.totals.mau) }}
                                    </div>
                                    <div class="kpi-sub">
                                        stickiness (DAU/MAU) <strong>{{ formatPercent(kpi.ga4.totals.stickiness) }}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Sessions per user -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">SESSIONS / USER</small>
                                    <div class="kpi-value">{{ kpi.ga4.totals.sessions_per_user }}</div>
                                    <div class="kpi-sub">{{ formatNumber(kpi.ga4.totals.sessions) }} sessions total</div>
                                </div>
                            </div>
                        </div>

                        <!-- Avg session duration -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">AVG SESSION DURATION</small>
                                    <div class="kpi-value">{{ formatDuration(kpi.ga4.totals.avg_session_duration) }}</div>
                                    <div class="kpi-sub">
                                        engagement/user {{ formatDuration(kpi.ga4.totals.avg_engagement_time) }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Active users -->
                        <div class="col-md-3">
                            <div class="panel panel-default kpi-card">
                                <div class="panel-body">
                                    <small class="text-muted">ACTIVE USERS</small>
                                    <div class="kpi-value">{{ formatNumber(kpi.ga4.totals.active_users) }}</div>
                                    <div class="kpi-sub">{{ formatNumber(kpi.ga4.totals.new_users) }} new in range</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ══ RETENTION + COMPARATIVE ADVANTAGE ════════════════ -->
                    <div class="row">
                        <div class="col-md-6">
                            <h4 class="kpi-section">Retention</h4>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="panel panel-default kpi-card">
                                        <div class="panel-body">
                                            <small class="text-muted">D7 RETENTION</small>
                                            <div class="kpi-value">{{ formatPercent(retentionD7) }}</div>
                                            <div class="kpi-sub">of new users still active at day 7</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="panel panel-default kpi-card">
                                        <div class="panel-body">
                                            <small class="text-muted">D30 RETENTION</small>
                                            <div class="kpi-value">
                                                <span v-if="retentionD30 !== null">{{ formatPercent(retentionD30) }}</span>
                                                <span v-else class="kpi-empty" title="Needs 30+ days of history">—</span>
                                            </div>
                                            <div class="kpi-sub">
                                                <span v-if="retentionD30 === null">builds with history</span>
                                                <span v-else>of new users active at day 30</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-md-6">
                            <h4 class="kpi-section">Comparative advantage</h4>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="panel panel-default kpi-card">
                                        <div class="panel-body">
                                            <small class="text-muted">AUTOMATED CONTENT</small>
                                            <div class="kpi-value">{{ formatPercent(kpi.local.content.automation_ratio) }}</div>
                                            <div class="kpi-sub">
                                                {{ formatNumber(kpi.local.content.articles_automated) }} auto ·
                                                {{ formatNumber(kpi.local.content.articles_manual) }} manual
                                                ({{ formatNumber(kpi.local.content.articles_total) }} total)
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="panel panel-default kpi-card">
                                        <div class="panel-body">
                                            <small class="text-muted">CONTENT COST</small>
                                            <div class="kpi-value">
                                                <span v-if="kpi.manual.content_cost !== null">{{ formatMoney(kpi.manual.content_cost, kpi.manual.currency) }}</span>
                                                <span v-else class="kpi-empty">—</span>
                                            </div>
                                            <div class="kpi-sub">
                                                <span v-if="kpi.manual.content_cost !== null && kpi.local.content.articles_total > 0">
                                                    {{ formatMoney(kpi.manual.content_cost / kpi.local.content.articles_total, kpi.manual.currency) }}/article
                                                </span>
                                                <a v-else href="#" @click.prevent="editing = true">set content cost</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ══ PLATFORM SPLIT ═══════════════════════════════════ -->
                    <div class="row">
                        <div class="col-md-8">
                            <div class="panel panel-default">
                                <div class="panel-heading"><strong>Platform split</strong> <small class="text-muted">(GA4, this range)</small></div>
                                <table class="table table-condensed">
                                    <thead>
                                        <tr>
                                            <th>Platform</th>
                                            <th class="text-right">Visitors</th>
                                            <th class="text-right">Active</th>
                                            <th class="text-right">Sessions</th>
                                            <th class="text-right">Sign-ups</th>
                                            <th class="text-right">Avg duration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in platformRows" :key="row.platform">
                                            <td><strong>{{ row.platform }}</strong></td>
                                            <td class="text-right">{{ formatNumber(row.total_users) }}</td>
                                            <td class="text-right">{{ formatNumber(row.active_users) }}</td>
                                            <td class="text-right">{{ formatNumber(row.sessions) }}</td>
                                            <td class="text-right">{{ formatNumber(row.signups) }}</td>
                                            <td class="text-right">{{ formatDuration(row.avg_session_duration) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <!-- Manual inputs editor -->
                        <div class="col-md-4">
                            <div class="panel panel-default">
                                <div class="panel-heading">
                                    <strong>Manual inputs</strong>
                                    <small class="text-muted">· {{ editPeriod }}</small>
                                    <button class="btn btn-xs btn-default pull-right" @click="editing = !editing">
                                        <i class="fal" :class="editing ? 'fa-times' : 'fa-pen'"></i>
                                        {{ editing ? 'Close' : 'Edit' }}
                                    </button>
                                </div>
                                <div class="panel-body">
                                    <template v-if="editing">
                                        <div class="form-group">
                                            <label>Ad spend ({{ editPeriod }})</label>
                                            <input type="number" min="0" step="0.01" v-model="inputAdSpend" class="form-control input-sm" placeholder="e.g. 1500">
                                        </div>
                                        <div class="form-group">
                                            <label>Content cost ({{ editPeriod }})</label>
                                            <input type="number" min="0" step="0.01" v-model="inputContentCost" class="form-control input-sm" placeholder="e.g. 800">
                                        </div>
                                        <div class="form-group">
                                            <label>Currency</label>
                                            <input type="text" maxlength="3" v-model="inputCurrency" class="form-control input-sm" style="width: 80px;">
                                        </div>
                                        <button class="btn btn-sm btn-primary" :disabled="saving" @click="saveInputs">
                                            <i v-if="saving" class="fal fa-spinner fa-spin"></i> Save
                                        </button>
                                    </template>
                                    <template v-else>
                                        <p class="text-muted" style="margin: 0;">
                                            CAC and content cost are entered per month here.
                                            Ad spend ÷ acquired users gives CAC.
                                        </p>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    </template>

                    <div v-else-if="!error" class="text-center" style="padding: 40px;">
                        <i class="fal fa-spinner fa-spin fa-3x text-muted"></i>
                        <p class="text-muted">Loading KPIs…</p>
                    </div>

                </div>
            </div>
        </section>
    </div>
</template>

<script>
import KpiMonitor from './KpiMonitor.js';

// A couple of derived bits live as computed here to keep the .js lean.
KpiMonitor.computed = Object.assign({}, KpiMonitor.computed, {
    downloadsTotal () {
        if (!this.kpi) return null;
        const d = this.kpi.downloads;
        if (d.total !== null && d.total !== undefined) return d.total;
        // Android pending → show iOS-only with the card sub-line explaining.
        return d.ios && d.ios.available ? d.ios.downloads : null;
    },
    retentionD7 () {
        return this.kpi && this.kpi.ga4.retention ? this.kpi.ga4.retention.d7 : null;
    },
    retentionD30 () {
        return this.kpi && this.kpi.ga4.retention ? this.kpi.ga4.retention.d30 : null;
    },
    platformMix () {
        if (!this.kpi) return '';
        const bp = this.kpi.ga4.by_platform || {};
        const parts = [];
        if (bp.iOS) parts.push('iOS ' + this.formatNumber(bp.iOS.total_users));
        if (bp.Android) parts.push('Android ' + this.formatNumber(bp.Android.total_users));
        if (bp.web) parts.push('Web ' + this.formatNumber(bp.web.total_users));
        return parts.join(' · ');
    },
});

export default KpiMonitor;
</script>

<style scoped>
    .wrapper {
        padding-left: 90px;
    }
    .breadcrumb {
        left: 92px;
    }
    .panel.panel-block .panel-heading {
        padding-left: 30px;
    }
    .kpi-controls {
        float: right;
        padding-top: 6px;
        font-size: 12px;
        color: #999;
    }
    .kpi-controls .updated {
        margin-left: 12px;
    }
    .custom-range {
        margin-left: 8px;
    }
    .inline-date {
        display: inline-block;
        width: 140px;
    }
    .kpi-section {
        margin: 14px 0 6px;
        font-weight: 600;
        color: #555;
        text-transform: uppercase;
        font-size: 13px;
        letter-spacing: .03em;
    }
    .kpi-card .panel-body {
        padding: 14px 16px;
    }
    .kpi-value {
        font-size: 30px;
        font-weight: 300;
        line-height: 1.15;
        white-space: nowrap;
    }
    .kpi-slash {
        color: #ccc;
        padding: 0 3px;
    }
    .kpi-sub {
        color: #999;
        font-size: 12px;
        margin-top: 2px;
    }
    .kpi-empty {
        color: #ccc;
    }
    .range-caption {
        margin: 4px 0 10px;
        font-size: 12px;
    }
</style>
